import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ManuscriptDto, ManuscriptCompositionDto, ManuscriptFragmentDto } from 'src/app/models/manuscript-details-dto';
import { TranslateLangPipe } from 'src/app/components/shared/pipes/translateLang.pipe';
import { ImageDto } from 'src/app/models/image-dto';
import { RoutingService } from 'src/app/services/routing.service';
import { SearchService } from 'src/app/services/search.service';
import { EntityType } from 'src/app/enums/entity-type.enum';

@Component({
  selector: 'manuscript-details',
  templateUrl: './manuscript-details.component.html',
  styleUrls: ['./manuscript-details.component.scss']
})
export class ManuscriptDetailsComponent implements OnInit, OnDestroy {
  private _selectedManuscript!: ManuscriptDto;
  @Input() set selectedManuscript(value: ManuscriptDto) {
    this._selectedManuscript = value;
    this.updateMatchedFragments();
  }
  get selectedManuscript(): ManuscriptDto {
    return this._selectedManuscript;
  }
  @Input() currentLanguage!: Observable<string>;
  
  currentLang: string = 'he';
  expandedCompositions: Set<number> = new Set();
  showAllCompositionDetails: boolean = false;
  compositionsSectionOpen: boolean = true;
  fragmentsSectionOpen: boolean = false;
  bibliographiesSectionOpen: boolean = false;

  // Fragments of this manuscript that matched the current search (fragments are hidden from the results list)
  matchedFragmentIds: Set<number> = new Set();
  sortedFragments: ManuscriptFragmentDto[] = [];
  private searchFragmentIds: Set<number> = new Set();
  private searchResultsSubscription?: Subscription;

  // Images for gallery
  get images(): ImageDto[] {
    return this.selectedManuscript?.manuscriptImages || [];
  }

  constructor(
    private translateLang: TranslateLangPipe,
    private routingService: RoutingService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.currentLanguage.subscribe(lang => {
      this.currentLang = lang;
    });
    this.searchResultsSubscription = this.searchService.searchResults$.subscribe(entities => {
      this.searchFragmentIds = new Set(
        entities.filter(e => e.entityType === EntityType.FRAGMENT).map(e => e.entityId)
      );
      this.updateMatchedFragments();
    });
    // Returning from one of the fragments - reopen the list it was opened from
    if (this.routingService.getReturnSection() === 'fragments') {
      this.fragmentsSectionOpen = true;
    }
    if(this.selectedManuscript?.manuscriptCompositions?.length === 1) {
      this.expandedCompositions.add(0);
    }
    // Initialize the switch state
    this.updateSwitchState();
  }

  toggleComposition(index: number): void {
    if (this.expandedCompositions.has(index)) {
      this.expandedCompositions.delete(index);
    } else {
      this.expandedCompositions.add(index);
    }
    // Update the switch state based on whether all compositions are expanded
    this.updateSwitchState();
  }

  isCompositionExpanded(index: number): boolean {
    return this.expandedCompositions.has(index);
  }
  
  toggleAllCompositionDetails(): void {
    this.showAllCompositionDetails = !this.showAllCompositionDetails;
    
    if (this.showAllCompositionDetails) {
      // Expand all compositions
      this.selectedManuscript?.manuscriptCompositions?.forEach((_, index) => {
        this.expandedCompositions.add(index);
      });
    } else {
      // Collapse all compositions
      this.expandedCompositions.clear();
    }
  }

  toggleCompositionsSection(): void {
    this.compositionsSectionOpen = !this.compositionsSectionOpen;
  }

  toggleFragmentsSection(): void {
    this.fragmentsSectionOpen = !this.fragmentsSectionOpen;
  }

  toggleBibliographiesSection(): void {
    this.bibliographiesSectionOpen = !this.bibliographiesSectionOpen;
  }
  private updateSwitchState(): void {
    const totalCompositions = this.selectedManuscript?.manuscriptCompositions?.length || 0;
    const expandedCount = this.expandedCompositions.size;
    this.showAllCompositionDetails = totalCompositions > 0 && expandedCount === totalCompositions;
  }

  getTranslatedValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object') {
      return this.currentLang === 'he' ? (value.hebrew || value.english || '') : (value.english || value.hebrew || '');
    }
    return '';
  }

  isFragmentMatched(fragment: ManuscriptFragmentDto): boolean {
    return this.matchedFragmentIds.has(Number(fragment.entityId));
  }

  private updateMatchedFragments(): void {
    const fragments = this.selectedManuscript?.manuscriptFragments || [];
    // Only free text can match part of a manuscript's fragments; other criteria match all or none of them
    const hasFreeText = !!this.searchService.searchFields?.freeText?.length;
    this.matchedFragmentIds = hasFreeText
      ? new Set(fragments.map(f => Number(f.entityId)).filter(id => this.searchFragmentIds.has(id)))
      : new Set();
    // Matched fragments first, original order otherwise (sort is stable)
    this.sortedFragments = [...fragments].sort(
      (a, b) => Number(this.isFragmentMatched(b)) - Number(this.isFragmentMatched(a))
    );
    if (this.matchedFragmentIds.size > 0) {
      this.fragmentsSectionOpen = true;
    }
  }

  ngOnDestroy(): void {
    this.searchResultsSubscription?.unsubscribe();
  }

  goToFragment(fragId: string): void {
    const queryParams = {
      "entityId": fragId,
      "entityType": EntityType.FRAGMENT
    };
    this.routingService.navigateToChild(queryParams, 'fragments:' + fragId);
  }
} 